import * as endpoints from "../../lib/api/endpoints";
import * as error from "../../lib/api/error";
import * as leadEvents from "../../constants/leadEvents";
import * as models from "../../lib/api/models";
import * as queryStringHelpers from "../../lib/queryStringHelpers";
import * as routes from "../../constants/routes";
import * as validationLib from "../../lib/validation";
import ActionButton, {ActionButtonStyle} from "../ActionButton";
import ApplicationForm from "../ApplicationForm";
import applicationToApplicationValidate from "../../lib/applicationToApplicationValidate";
import callApplicationLead from "../../lib/callApplicationLead";
import CardPicker from "../CardPicker";
import Checkbox from "../Checkbox";
import cn from "../../lib/classnames";
import CollegeInput from "../CollegeInput";
import createDebouncedAppLead from "../../lib/api/createDebouncedAppLead";
import ElectronicCommunicationsAgreementModal from "../ElectronicCommunicationsAgreementModal";
import ErrorBoundary from "../ErrorBoundary";
import Footer from "../Footer";
import getFirstInvalidKey, {ValidationType} from "../../lib/getFirstInvalidKey";
import getFullName from "../../lib/getFullName";
import getValidationKey from "../../lib/getValidationKey";
import hasFailingValidation from "../../lib/hasFailingValidation";
import Header from "../Header";
import IdleTimeoutWrapper from "../IdleTimeoutWrapper";
import isAddressKey from "../../lib/isAddressKey";
import isApplicationKey from "../../lib/isApplicationKey";
import Modal from "../Modal";
import React, {useState, useEffect, useRef} from "react";
import SetNoIndex from "../../lib/SetNoIndex";
import styles from "./InviteApplicationPage.module.css";
import useFocusInvalid from "../../lib/useFocusInvalid";
import usePageView from "../../lib/usePageView";
import validateApplicationField from "../../lib/validateApplicationField";
import {Application, AppKeys, AddressKeys, AddressValidationKeys} from "../../lib/api/models";
import {RouteComponentProps} from "react-router-dom";
import {StaticContext} from "react-router";
import CheckboxEnhanced from "../ CheckboxEnhanced";
import {getCards} from "../../lib/getCards";
import LegalInfo from "../LegalInfo";
import GalleryView from "../GalleryView";

const IS_DEV = process.env.NODE_ENV === "development";

const EMPTY_APP: Application = {
    [AppKeys.Email]: IS_DEV ? "dev@thebhwgroup.com" : "",
    [AppKeys.FirstName]: IS_DEV ? "John" : "",
    [AppKeys.MiddleInitial]: IS_DEV ? "A" : "",
    [AppKeys.LastName]: IS_DEV ? "Smith" : "",
    [AppKeys.Address]: {
        [AddressKeys.AddressLine1]: IS_DEV ? "123 Some St" : "",
        [AddressKeys.AddressLine2]: "",
        [AddressKeys.City]: IS_DEV ? "Austin" : "",
        [AddressKeys.State]: IS_DEV ? "TX" : "",
        [AddressKeys.Zipcode]: IS_DEV ? "78757" : "",
    },
    [AppKeys.StudentStatus]: IS_DEV ? models.StudentStatus.HSEntering : null,
    [AppKeys.PrimaryPhone]: IS_DEV ? "(123) 123-1234" : "",
    [AppKeys.DateOfBirth]: IS_DEV ? "01/01/2000" : "",
    [AppKeys.SocialSecurity]: IS_DEV ? "123-12-1234" : "",
    [AppKeys.College]: IS_DEV ? "Texas A&M University" : "",
    [AppKeys.SavingsAccount]: IS_DEV ? true : null,
    [AppKeys.CheckingAccount]: IS_DEV ? true : null,
    [AppKeys.PaymentAccount]: IS_DEV ? "checking" : "",
    [AppKeys.CardSelection]: "",
    [AppKeys.AccountTotal]: IS_DEV ? "$1,000" : "",
    [AppKeys.AnnualGrossIncome]: IS_DEV ? "$1,000" : "",
    [AppKeys.Residency]: IS_DEV ? true : null,
    [AppKeys.AnnualObligation]: "",
    [AppKeys.UrlCode]: null,
    [AppKeys.InviteCode]: null,
    [AppKeys.ReferralCode]: null,
};

const strings = {
    Address: "Address",
    agreeToInfoText: 'By checking this box and clicking "Submit" below:',
    authorization:
        "Authorization for the Social Security Administration to Disclose Your Social Security Number Verification.",
    authorization2:
        "I authorize the Social Security Administration (SSA) to verify and disclose to 1st Financial Bank USA, Student Center, P.O. Box 1050, N. Sioux City, SD 57049 and SentiLink, as its service provider, for the purpose of this transaction whether the name, Social Security Number (SSN) and date of birth I have submitted matches information in SSA records. My consent is for a one-time validation within the next 60 days.",
    inviteCollegeText: "College (future or present)",
    signing:
        "I am signing the consent for SSA to disclose my SSN Verification to 1st Financial Bank USA and SentiLink. I agree that my electronic signature has the same legal meaning, validity, and effect as my handwritten signature.",
    title: "1st Financial Bank USA Card Invitation Application",
    updateAddress: "Update My Address",
    updateAddressLine1:
        "If you update your address, we will attempt to verify your identity by comparing it with information obtained from a credit bureau or other trusted third-party source.  We may also ask you for additional documentation.",
    updateAddressLine2:
        "Select “Update My Address” to edit this address or cancel to keep this address.",
    updateMyAddress: "Update My Address",
    paperlessTitle: "Electronic Communications - ",
    paperlessLink: "Electronic Communications Agreement",
    paperlessText:
        "By checking this box, you agree that we may provide your application decision, and if approved, your 1st Financial Bank USA card account disclosures and other communications electronically rather than by paper. You have the right to receive the information in paper and may withdraw your agreement at any time. Click Electronic Communications Agreement for more information, including how to access electronic communications, withdraw your consent, update your email address, or request a paper copy of information provided electronically.",
    submit: "Submit",
};

type RouterState = {
    firstName?: string;
    middleInitial?: string;
    lastName?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    bindingID?: string;
    id?: string;
    invitationCode?: string;
    cardTypes?: string[];
    attributes?: {
        multiImageSlider?: boolean;
        defaultCardType?: string;
    };
};

const getDefaultCardSelection = (
    availableCardTypes: string[] | null,
    defaultCardType: string | null,
) => {
    if (availableCardTypes) {
        const {cards} = getCards(availableCardTypes, defaultCardType ?? undefined);
        if (cards.length > 0) {
            return cards[0].value;
        }
    }
    return "";
};

export default function InviteApplicationPage(
    props: RouteComponentProps<undefined, StaticContext, RouterState>,
) {
    const stateValue = props.location.state;

    let firstNameValue = "";
    let middleInitialValue = "";
    let lastNameValue = "";
    let addressValue = "";
    let addressValue2: string | null = "";
    let cityValue = "";
    let usStateValue = "";
    let zipCodeValue = "";
    let inviteCode: string | null = "";
    let cardTypes: string[] | null = null;
    let defaultCardType: string | null = null;
    let defaultCardBrand: "VISA" | "MC" = "VISA";
    let showGalleryView = false;
    if (stateValue) {
        firstNameValue = stateValue.firstName ? stateValue.firstName : EMPTY_APP[AppKeys.FirstName];
        middleInitialValue = stateValue.middleInitial
            ? stateValue.middleInitial
            : EMPTY_APP[AppKeys.MiddleInitial];
        lastNameValue = stateValue.lastName ? stateValue.lastName : EMPTY_APP[AppKeys.LastName];
        addressValue = stateValue.addressLine1
            ? stateValue.addressLine1
            : EMPTY_APP[AppKeys.Address][AddressKeys.AddressLine1];
        addressValue2 = stateValue.addressLine2
            ? stateValue.addressLine2
            : EMPTY_APP[AppKeys.Address][AddressKeys.AddressLine2];
        cityValue = stateValue.city
            ? stateValue.city
            : EMPTY_APP[AppKeys.Address][AddressKeys.City];
        usStateValue = stateValue.state
            ? stateValue.state
            : EMPTY_APP[AppKeys.Address][AddressKeys.State];
        zipCodeValue = stateValue.zipCode
            ? stateValue.zipCode
            : EMPTY_APP[AppKeys.Address][AddressKeys.Zipcode];
        inviteCode = stateValue.invitationCode
            ? stateValue.invitationCode
            : EMPTY_APP[AppKeys.InviteCode];
        cardTypes = stateValue.cardTypes ?? [];
        defaultCardType = stateValue.attributes?.defaultCardType ?? null;
        defaultCardBrand = stateValue.cardTypes?.find((t) => t.endsWith("_MC")) ? "MC" : "VISA";
        showGalleryView = !!stateValue.attributes?.multiImageSlider;
    }

    const windowUrl = window.location.href;

    const [validation, setValidation] = useState<validationLib.Validation>({});

    const [application, setApplication] = useState({
        ...EMPTY_APP,
        [AppKeys.FirstName]: firstNameValue,
        [AppKeys.MiddleInitial]: middleInitialValue,
        [AppKeys.LastName]: lastNameValue,
        [AppKeys.Address]: {
            [AddressKeys.AddressLine1]: addressValue,
            [AddressKeys.AddressLine2]: addressValue2,
            [AddressKeys.City]: cityValue,
            [AddressKeys.State]: usStateValue,
            [AddressKeys.Zipcode]: zipCodeValue,
        },
        [AppKeys.InviteCode]: inviteCode,
        [AppKeys.CardSelection]: getDefaultCardSelection(cardTypes, defaultCardType),
    });
    const [isEditAddressMode, setIsEditAddressMode] = useState(false);
    const [showExpandedAddress, setShowExpandedAddress] = useState(false);
    const [updateAddressModalOpen, setUpdateAddressModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [validationCount, setValidationCount] = useState(3);
    const [ECBSVConsent, setECBSVConsent] = useState(false);
    const [isPaperless, setIsPaperless] = useState(false);
    const [isPaperlessOpen, setIsPaperlessOpen] = useState(false);

    const isMountedRef = useRef<boolean>(false);
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        document.title = strings.title;
    }, []);

    const appInviteCode = application[AppKeys.InviteCode];
    useEffect(() => {
        if (appInviteCode) {
            callApplicationLead(leadEvents.FORM_VIEW, appInviteCode, window.location.href);
        }
    }, [appInviteCode]);

    SetNoIndex();
    usePageView(strings.title);

    const submitButtonRef = useRef<HTMLButtonElement>(null);
    const collegeRef = useRef<HTMLInputElement | null>(null);

    const debouncedAppLead = useRef(
        createDebouncedAppLead(EMPTY_APP, application[AppKeys.InviteCode], windowUrl),
    );

    useEffect(() => {
        Promise.resolve()
            .then(() => debouncedAppLead.current(application))
            .catch((err) => error.reportAxios(err, "InviteApplicationPage"));
    }, [application]);

    useEffect(() => {
        if (isEditAddressMode && !updateAddressModalOpen) {
            setShowExpandedAddress(true);
        }
    }, [isEditAddressMode, updateAddressModalOpen]);

    // Validation Focusing

    const firstInvalidKey = getFirstInvalidKey(validation, ValidationType.Application);
    const firstInvalidRef = firstInvalidKey === AppKeys.College ? collegeRef : undefined;
    useFocusInvalid(validationCount, firstInvalidRef?.current);

    if (
        !props.location.state ||
        !props.location.state.firstName ||
        !props.location.state.lastName ||
        !props.location.state.addressLine1 ||
        !props.location.state.city ||
        !props.location.state.state ||
        !props.location.state.zipCode ||
        !props.location.state.bindingID ||
        !props.location.state.invitationCode ||
        !props.location.state.cardTypes ||
        props.location.state.cardTypes.length < 1
    ) {
        props.history.replace(routes.INVITE_LANDING);
        return null;
    }

    function getAddressValue1(ad1: string | undefined, ad2: string | null | undefined): string {
        let address = ad1 ? ad1 : "";
        if (ad2) {
            address = address ? `${address} ${ad2}` : `${ad2}`;
        }
        return address;
    }

    function getAddressValue2(
        city: string | undefined,
        state: string | undefined,
        zipcode: string | undefined,
    ) {
        let address = city ? city : "";
        if (state) {
            address = address ? `${address}, ${state}` : state;
        }
        if (zipcode) {
            address = address ? `${address} ${zipcode}` : zipcode;
        }
        return address;
    }

    function onBlurInput(e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;
        if (isApplicationKey(name) || isAddressKey(name)) {
            const {isValid, messages, validationKey} = validateApplicationField(name, value);
            if (isValid) {
                return;
            }

            if (validationKey !== null) {
                setValidation((prev) => ({
                    ...prev,
                    errors: {
                        ...prev?.errors,
                        [validationKey]: messages,
                    },
                }));
            }
        }
    }

    function submitApplication(econsent?: boolean) {
        if (hasFailingValidation(validation)) {
            return;
        }

        if (econsent === undefined && !isPaperless) {
            setIsPaperlessOpen(true);
            return;
        }

        const parsedApplication = applicationToApplicationValidate(
            application,
            true,
            defaultCardBrand,
        );
        setLoading(true);

        callApplicationLead(leadEvents.FORM_VALIDATE, application[AppKeys.InviteCode], windowUrl);
        endpoints
            .applicationValidate(parsedApplication)
            .then(() => {
                callApplicationLead(
                    leadEvents.FORM_SUBMIT,
                    application[AppKeys.InviteCode],
                    windowUrl,
                );
                const agreedPaperless = econsent !== undefined ? econsent : isPaperless;
                return endpoints.applicationSubmit(
                    parsedApplication,
                    agreedPaperless,
                    ECBSVConsent,
                );
            })
            .then((data) => {
                if (!isMountedRef.current) {
                    error.reportUnexpectedNavigation(
                        new Error("User navigated away from application during submission."),
                    );
                    return;
                }
                const {applicationId, status, instantCard} = data;
                let queryString = queryStringHelpers.addReferrerGCLID(
                    window.location.search,
                    document.referrer,
                );

                if (status === models.ApplicationStatus.Approved && applicationId) {
                    // If instantCard is true, set in query string params
                    // Even though this is not secure, changing the query string param
                    // Only allows the user to make a request for the instant card info
                    // Any invalid requests would return no values
                    if (instantCard) {
                        const qs = queryStringHelpers.objectify(queryString);
                        qs[queryStringHelpers.Keys.InstantCard] = "true";
                        queryString = queryStringHelpers.stringify(qs);
                    }

                    props.history.replace(`${routes.CONGRATULATIONS}${queryString}`, {
                        id: applicationId,
                    });
                    return;
                }

                let destination = routes.PROCESSING;
                switch (status) {
                    case models.ApplicationStatus.Duplicate:
                        destination = routes.DUPLICATE;
                        break;
                    case models.ApplicationStatus.DuplicateDsn:
                        destination = routes.DUPLICATE_DSN;
                        break;
                    case models.ApplicationStatus.ThankYou:
                        destination = routes.THANK_YOU;
                        break;
                    case models.ApplicationStatus.Secured:
                        destination = routes.SECURED_CARD;
                }

                if (applicationId) {
                    queryString = queryStringHelpers.addDSN(queryString, applicationId);
                }
                if (destination === routes.PROCESSING) {
                    const qs = queryStringHelpers.objectify(queryString);
                    qs[queryStringHelpers.Keys.CardBrand] = defaultCardBrand;
                    queryString = queryStringHelpers.stringify(qs);
                }
                const fullDestination = `${process.env.REACT_APP_BASE_PATH}${destination}${queryString}`;
                // NOTE: Using full round-trip page transition here for security reasons
                window.location.replace(fullDestination);
            })
            .catch((err: validationLib.Validation) => {
                if (!isMountedRef.current) {
                    return;
                }
                const errValidation = validationLib.getValidationObj(err);
                setValidation(errValidation);
                if (errValidation.errors) {
                    Object.keys(errValidation.errors).forEach((key) => {
                        if (
                            Object.values(models.AddressValidationKeys).find(
                                (value) => key === value,
                            )
                        ) {
                            setIsEditAddressMode(true);
                        }
                    });
                }
            })
            .finally(() => {
                if (!isMountedRef.current) {
                    return;
                }
                setLoading(false);
                setValidationCount((prev) => prev + 1);
            });
    }

    function clearValidation(validationKey: AppKeys | AddressKeys | AddressValidationKeys) {
        setValidation((prev) => {
            if (prev.generic || (prev.errors && prev.errors[validationKey])) {
                const nextValidation = {
                    ...prev,
                };
                delete nextValidation.generic;
                if (prev && prev.errors) {
                    delete prev.errors[validationKey];
                }
                return nextValidation;
            }
            return prev;
        });
    }

    function onChangeApplicationField(name: AppKeys | AddressKeys, value: string | boolean) {
        if (isAddressKey(name)) {
            setApplication((prevApp) => ({
                ...prevApp,
                [AppKeys.Address]: {
                    ...prevApp[AppKeys.Address],
                    [name]: value,
                },
            }));
            const validationKey = getValidationKey(name as AddressKeys);
            clearValidation(validationKey);
        } else {
            setApplication((prevApp) => ({
                ...prevApp,
                [name]: value,
            }));
            clearValidation(name);
        }
    }

    // Render existing address information
    function renderAddress(expandAddressGroupOnUpdate: () => void) {
        const addressLine1 = getAddressValue1(addressValue, addressValue2);
        const addressLine2 = getAddressValue2(cityValue, usStateValue, zipCodeValue);
        return (
            <div className={styles.addressContainer}>
                <div className={styles.addressLabel}>{strings.Address}</div>
                <div className={cn(styles.addressPrefilledLine1)}>{addressLine1}</div>
                {addressLine2 ? (
                    <div className={cn(styles.addressPrefilledLine2)}>{addressLine2}</div>
                ) : null}
                <div>
                    <ActionButton
                        label={strings.updateAddress}
                        onClick={() => setUpdateAddressModalOpen(true)}
                        className={styles.addressButton}
                        ref={submitButtonRef}
                        style={ActionButtonStyle.outlinedSmall}
                        type="button"
                    />
                </div>
                <Modal
                    id="update-address-modal"
                    label={strings.updateAddress}
                    isOpen={updateAddressModalOpen}
                    onClose={() => setUpdateAddressModalOpen(false)}
                    onSubmit={() => {
                        setIsEditAddressMode(true);
                        setUpdateAddressModalOpen(false);
                        expandAddressGroupOnUpdate();
                    }}
                    onSubmitLabel={strings.updateMyAddress}
                    onCancel={() => setUpdateAddressModalOpen(false)}
                    showCancelButton={true}
                    triggerElement={submitButtonRef.current}
                    focusFooterOnShow={true}
                >
                    <div className={styles.addressLine}>{strings.updateAddressLine1}</div>
                    <div className={styles.addressLine}>{strings.updateAddressLine2}</div>
                </Modal>
            </div>
        );
    }

    function renderCardPicker() {
        let validationMessage: string[] | undefined;
        if (validation.errors && validation.errors[AppKeys.CardSelection]) {
            validationMessage = validation.errors[AppKeys.CardSelection];
        }
        if (!cardTypes) {
            return;
        }
        return (
            <div className={styles.cardPicker}>
                <CardPicker
                    cardId={application[AppKeys.CardSelection]}
                    onChange={(cardId: string) =>
                        onChangeApplicationField(AppKeys.CardSelection, cardId)
                    }
                    name={getFullName(
                        application[AppKeys.FirstName],
                        "",
                        application[AppKeys.LastName],
                    )}
                    validationMessage={validationMessage}
                    cardTypes={cardTypes}
                />
            </div>
        );
    }

    function renderCollegeInput() {
        let validationMessage: string[] | undefined;
        if (validation.errors && validation.errors[AppKeys.College]) {
            validationMessage = validation.errors[AppKeys.College];
        }
        return (
            <CollegeInput
                id={AppKeys.College}
                name={AppKeys.College}
                onCollegeSelected={(collegeName) => {
                    onChangeApplicationField(models.AppKeys.College, collegeName);
                }}
                onBlur={onBlurInput}
                label={strings.inviteCollegeText}
                ref={collegeRef}
                required={true}
                validationMessage={validationMessage}
                initialSearch={application[AppKeys.College]}
            />
        );
    }

    function renderAgreement() {
        return (
            <CheckboxEnhanced
                classNameLabel={styles.checkboxLabel}
                className={styles.checkbox}
                id="checkbox-1"
                label={strings.agreeToInfoText}
                isChecked={ECBSVConsent}
                onChange={(e) => {
                    setECBSVConsent(e.target.checked);
                    callApplicationLead(
                        "invite-form-ecbsv-checkbox",
                        application[AppKeys.InviteCode],
                        windowUrl,
                        e.target.checked.toString(),
                    );
                }}
                hasGradient={true}
            >
                <ul>
                    <li>
                        <div className={cn(styles.bold, styles.listItem)}>
                            {strings.authorization}
                        </div>
                        <li className={cn(styles.listItem, styles.indent)}>
                            {strings.authorization2}
                        </li>
                    </li>
                    <li className={cn(styles.listItem, styles.indent)}>{strings.signing}</li>
                </ul>
            </CheckboxEnhanced>
        );
    }

    function renderPaperless() {
        return (
            <>
                <Checkbox
                    label={strings.paperlessText}
                    isChecked={isPaperless}
                    onChange={(e) => setIsPaperless(e.target.checked)}
                    className={styles.checkbox}
                    classNameLabel={styles.checkboxLabel}
                    hasGradient={true}
                    optionalFrontBoldLabel={strings.paperlessTitle}
                    linkText={strings.paperlessLink}
                    to={routes.ELECTRONIC_AGREEMENT_FULL}
                />
                <ElectronicCommunicationsAgreementModal
                    isOpen={isPaperlessOpen}
                    onClose={() => setIsPaperlessOpen(false)}
                    onSubmit={(agreed: boolean) => {
                        submitApplication(agreed);
                        setIsPaperlessOpen(false);
                    }}
                    triggerElement={submitButtonRef.current}
                />
            </>
        );
    }

    return (
        <ErrorBoundary>
            <IdleTimeoutWrapper
                newApplicationRoute={routes.INVITE_LANDING}
                focusAfterContinue={null}
            >
                <Header />
                <div className={styles.innerContainer}>
                    <ApplicationForm
                        application={application}
                        continueLabel={strings.submit}
                        firstInvalidKey={firstInvalidKey}
                        isRatesVisible={false}
                        loading={loading}
                        onBlurInput={onBlurInput}
                        onChangeApplication={onChangeApplicationField}
                        onContinue={submitApplication}
                        renderAddress={!showExpandedAddress ? renderAddress : undefined}
                        renderCardPicker={renderCardPicker}
                        renderCollegeInput={renderCollegeInput}
                        validation={validation}
                        validationCount={validationCount}
                        renderAgreement={renderAgreement}
                        renderPaperless={renderPaperless}
                        defaultCardBrand={defaultCardBrand}
                    />
                    {showGalleryView ? <GalleryView cardTypes={cardTypes ?? []} /> : null}
                </div>
                <LegalInfo renderInterestRates={false} />
                <Footer />
            </IdleTimeoutWrapper>
        </ErrorBoundary>
    );
}
